import { Avatar , Box , Skeleton , Typography } from '@mui/material';
import { StyledSpanEmptyStatus, StyledStatusChip } from 'dashboard409a/components/ValuationsTable/common';
import { getInitials } from 'utilities';
import { getModelStatusString, ModelWithValuationData } from '../../../services/models.types';
import { ModelsTableRowProps } from '../ModelsTable.types';

// Helper function to truncate text
const truncateText = (text: string | null | undefined, maxLength: number = 50): string => {
  if (!text || text === '-') {
    return text || '-';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return `${text.substring(0, maxLength).trim()}...`;
};

// Helper function to get full name from user object
export const getFullName = (user: unknown): string => {
  const userObj = user as { first_name?: string; last_name?: string };
  return `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim();
};
  
// Helper function to render notes cell
const renderNotesCell = (modelRow: ModelsTableRowProps['modelRow']) => {
  // Check if we have the new note structure
  if (modelRow.note) {
    return <Typography variant="body2">{truncateText(modelRow.note.content)}</Typography>;
  }
  // Fallback to old notes field for backward compatibility
  return <Typography variant="body2">{truncateText(modelRow.notes)}</Typography>;
};
  
// Helper function to render valuation lead cell
const renderValuationLeadCell = (modelRow: ModelsTableRowProps['modelRow']) => {
  if (modelRow.is_loading_valuation_data) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Skeleton variant="circular" width={28} height={28} />
        <Box>
          <Skeleton variant="text" width={120} height={20} />
        </Box>
      </Box>
    );
  }
  
  const valuationLead = modelRow.valuation_lead;
  const valuationLeadImageSrc = modelRow.valuation_lead_image_src;
  if (valuationLead && valuationLead !== '-') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Avatar
          alt={valuationLead}
          src={valuationLeadImageSrc}
          sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
          {getInitials({ name: valuationLead })}
        </Avatar>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.2 }}>
            {valuationLead}
          </Typography>
        </Box>
      </Box>
    );
  }
  
  return (
    <Typography variant="body2" color="text.secondary">
        -
    </Typography>
  );
};

// Helper function to render status cell content
interface RenderStatusCellProps {
  modelRow: ModelWithValuationData;
  isEditingStatus: boolean;
  isUpdatingStatus: boolean;
  isFinalStatus: boolean;
  onStatusClick: (e: React.MouseEvent) => void;
  onEditEnd: () => void;
}

export const renderStatusCell = ({
  modelRow,
  isEditingStatus,
  isUpdatingStatus,
  isFinalStatus,
  onStatusClick,
  onEditEnd,
}: RenderStatusCellProps) => {
  if (isEditingStatus) {
    return null;
  }

  const commonClickHandler = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!isUpdatingStatus && !isFinalStatus) {
      onStatusClick(e);
    }
  };

  const commonStyle = {
    cursor: isUpdatingStatus || isFinalStatus ? 'not-allowed' : 'pointer',
    opacity: isUpdatingStatus ? 0.6 : 1,
  };

  if (modelRow.model_status !== undefined) {
    return (
      <StyledStatusChip
        label={isUpdatingStatus ? 'Updating...' : getModelStatusString(modelRow.model_status)}
        status={getModelStatusString(modelRow.model_status)}
        onClick={commonClickHandler}
        style={commonStyle}
        title={isFinalStatus ? 'Final status cannot be changed' : undefined}
      />
    );
  }

  return (
    <StyledSpanEmptyStatus
      onClick={commonClickHandler}
      style={{
        ...commonStyle,
        color: '#666',
        fontStyle: 'italic',
      }}>
      {isUpdatingStatus ? 'Updating...' : 'Click to select status'}
    </StyledSpanEmptyStatus>
  );
};
  
export { renderNotesCell, renderValuationLeadCell };